/* LAB-52 · Size finder v3 (LAB-180, 2026-10-22) — the rule of record is scripts/size_reco.py; this file copies its
 * constants and must give the same answer for the six calibration points (the harness checks).
 *
 *   body chest (cm) = 1.152162 * kg - 0.00288 * kg² - 0.221899 * cm + 67.415   (ANSUR II, 6,068 measured adults)
 *   ease (Standard) = 16.5 cm for every body; Roomier = + 7 cm, capped at 22 cm of room (v3, LAB-180: chosen
 *                     on 6,068 measured ANSUR II bodies; the old ease grew with weight and height)
 *   nearest garment circumference (2 x flat chest) to chest + ease wins
 *   toss-up (runner-up within 1 cm) -> the bigger size, smaller named as the closer fit; a stated usual
 *   size that is one of the two breaks the tie instead
 *   length floor: the size's body length >= 0.37 x height, else step up
 *   kids: height band -> the size named by that height; outside 115-155 cm say so
 *
 * The block is server-rendered by snippets/lb-size-reco.liquid (custom-liquid block after the variant
 * picker). This script: runs the rule, renders the result cards, marks the suggested size button in the
 * picker (an attribute on the label, re-applied after Horizon's picker morph), offers an explicit Select,
 * and remembers the shopper's numbers in localStorage. Events go to PostHog when it is loaded and to
 * window.__lbReco always (the harness reads that).
 */
(function () {
  'use strict';
  var ADULT = [['XS', 47, 64], ['S', 50, 66], ['M', 53, 69], ['L', 56, 72], ['XL', 60, 75], ['2XL', 64, 77], ['3XL', 68, 77], ['4XL', 72, 77.5]];
  var KIDS = [[120, 115, 125], [130, 125, 135], [140, 135, 145], [150, 145, 155]];
  var ANSUR = [1.152162, -0.221899, 67.415281, -0.00288];   // + kg² term (v3, LAB-180)
  var EASE0 = 16.5, EASE_H = 0, EASE_W = 0, RELAXED_EXTRA = 7, BETWEEN_CM = 1.0;
  var LENGTH_FLOOR = 0.37;
  // size audit 2026-09-28 (analysis/sizing/2026-09-28-size-audit.md): Boxy/Oversized go one down from the tee size when
  // that size still leaves BIG_MIN_EASE cm around the body ("runs one size big" on the fit card, now also in the answer);
  // a garment whose chest is under body + FIT_MIN_EASE is never presented as a fit.
  // research check 2026-09-28 (analysis/sizing/2026-09-28-boxy-oversized-research.md): step down only when the tee-letter
  // size leaves more than BIG_TRIGGER cm around the body AND the smaller size still leaves BIG_MIN_EASE (30, not 20:
  // ANSUR under-estimates men 35+ by ~1.8 cm); brands keep the usual size for the oversized look, so this is the exception.
  var BIG_TRIGGER = 40, BIG_MIN_EASE = 30, FIT_MIN_EASE = 4;
  var ROOMIER_MAX_EASE = 22, FIT_MIN_EASE_WOMEN = -2, JACKET_MIN_ROOM = 12, JACKET_LENGTH_FLOOR = 0.335;   // LAB-168, see scripts/size_reco.py
  var JACKET_CODES = ['UT0142', 'RQ0034'];                                    // denim jacket, bomber
  var STEP_DOWN_CUTS = ['Boxy', 'Oversized'];
  var ONE_DOWN_CUTS = ['Oversized'];   // v2.1 (LAB-178, owner 10-21): only the Oversized Hoodie goes one down; Boxy = tee letter
  // v2.1 (LAB-178): never one down (or "one down sits closer") into a size shorter than LENGTH_FLOOR x height. UT0044 is
  // cut short on a very wide chest; stepping down gave 170/70 an M and 180/80 an L (owner: L is the 170/70 size).
  function longEnough(row, h) { return row[2] == null || row[2] >= LENGTH_FLOOR * h; }
  // ---- bottoms (wave 3, 2026-10-08) — port of scripts/size_reco.py recommend_bottom ------------------------------
  // body waist / hip from height + weight (ANSUR II fits; men +3 cm civilian; women's waist moved to the natural waist),
  // the brands' consensus body chart gives a size (the larger of waist and hip), then the garment's own hip must leave
  // BOTTOM_MIN_HIP_EASE cm, else one up. Research: analysis/sizing/2026-10-08-bottoms/.
  var BODY_FIT = { men: { waist: [102.49, -0.436, 0.796, 3.0], hip: [80.84, -0.140, 0.535, 0] },
                   women: { waist: [101.41, -0.472, 0.908, -6.6], hip: [84.69, -0.178, 0.686, 0] } };
  var LETTER_WAIST = { men: [['XS', 76], ['S', 81], ['M', 87], ['L', 95], ['XL', 104], ['2XL', 113], ['3XL', 122], ['4XL', 999]],
                       women: [['XS', 66], ['S', 71], ['M', 76], ['L', 83], ['XL', 91], ['2XL', 99], ['3XL', 107], ['4XL', 999]] };
  var LETTER_HIP = { men: [['XS', 90], ['S', 96], ['M', 102], ['L', 109], ['XL', 117], ['2XL', 125], ['3XL', 133], ['4XL', 999]],
                     women: [['XS', 91], ['S', 96], ['M', 101], ['L', 107], ['XL', 115], ['2XL', 123], ['3XL', 131], ['4XL', 999]] };
  var NUMERIC_HIP = [['0', 88], ['2', 91], ['4', 94], ['6', 97], ['8', 100], ['10', 103], ['12', 107], ['14', 111], ['16', 999]];
  var BOTTOM_MIN_HIP_EASE = 4, BOTTOM_LEN_FRAC = 0.55;
  var STORE_KEY = 'lb_reco_v1';
  var REF_TEES = [{"id":"gildan-5000","name":"Gildan Heavy Cotton","group":"Unisex","sizes":[["S",91.4],["M",101.6],["L",111.8],["XL",121.9],["2XL",132.1],["3XL",142.2],["4XL",152.4]]},{"id":"comfort-colors-1717","name":"Comfort Colors heavyweight (1717)","group":"Unisex","sizes":[["S",92.7],["M",102.9],["L",111.8],["XL",121.9],["2XL",132.1],["3XL",141.0],["4XL",151.1]]},{"id":"bella-3001","name":"Bella+Canvas 3001","group":"Unisex","sizes":[["XS",83.8],["S",91.4],["M",101.6],["L",111.8],["XL",121.9],["2XL",132.1],["3XL",142.2],["4XL",152.4]]},{"id":"next-level-3600","name":"Next Level 3600","group":"Unisex","sizes":[["XS",88.9],["S",96.5],["M",104.1],["L",111.8],["XL",121.9],["2XL",132.1],["3XL",142.2],["4XL",152.4]]},{"id":"carhartt-k87","name":"Carhartt K87 pocket T-shirt","group":"Unisex","sizes":[["S",106.7],["M",116.8],["L",127.0],["XL",137.2],["2XL",147.3],["3XL",157.5],["4XL",167.6]]},{"id":"uniqlo-u-crew","name":"Uniqlo U Crew Neck","group":"Unisex","sizes":[["XXS",94],["XS",100],["S",106],["M",112],["L",120],["XL",128],["XXL",136],["3XL",144]]},{"id":"uniqlo-supima","name":"Uniqlo Supima Cotton Crew Neck","group":"Unisex","sizes":[["XXS",91.0],["XS",97.0],["S",103.0],["M",109.0],["L",117.0],["XL",125.0],["XXL",131.0],["3XL",139.0]]},{"id":"gildan-5000l","name":"Gildan Heavy Cotton women's","group":"Women's","sizes":[["S",87.6],["M",97.8],["L",108.0],["XL",118.1],["2XL",128.3],["3XL",138.4]]},{"id":"gildan-64000l","name":"Gildan Softstyle women's","group":"Women's","sizes":[["S",86.4],["M",96.5],["L",106.7],["XL",116.8],["2XL",127.0],["3XL",137.2]]}];   // GENERATED from creatives/sizing/reference-tees.json (garment charts, sources there)
  var KID_STALE_DAYS = 180;
  var SIZES = ADULT.map(function (s) { return s[0]; });

  function bodyChest(h, w) { return ANSUR[0] * w + ANSUR[1] * h + ANSUR[2] + ANSUR[3] * w * w; }
  function ease(h, w, fit) { return EASE0 + EASE_H * (h - 170) + EASE_W * (w - 65) + (fit === 'relaxed' ? RELAXED_EXTRA : 0); }

  function teePick(h, w, fit, usual) {
    var target = bodyChest(h, w) + ease(h, w, fit);
    var ranked = ADULT.slice().sort(function (a, b) { return Math.abs(2 * a[1] - target) - Math.abs(2 * b[1] - target); });
    var best = ranked[0], second = ranked[1];
    var between = (Math.abs(2 * second[1] - target) - Math.abs(2 * best[1] - target)) < BETWEEN_CM;
    var tieByUsual = false;
    if (between) {
      if (usual && (usual === best[0] || usual === second[0])) {           // the shopper's own habit breaks the tie
        if (usual === second[0]) { var u = best; best = second; second = u; }
        tieByUsual = true;
      } else if (second[1] > best[1]) { var t = best; best = second; second = t; }   // better a bit loose than tight
    }
    var bumped = false;
    while (best[2] < LENGTH_FLOOR * h && ADULT.indexOf(best) < ADULT.length - 1) {
      best = ADULT[ADULT.indexOf(best) + 1]; between = false; bumped = true;
    }
    var next = ADULT.indexOf(best) < ADULT.length - 1 ? ADULT[ADULT.indexOf(best) + 1][0] : null;
    var usualGap = (usual && SIZES.indexOf(usual) >= 0) ? SIZES.indexOf(usual) - SIZES.indexOf(best[0]) : 0;   // + = usually wears bigger
    return { size: best[0], flat: best[1], length: best[2], between: between, alt: between ? second[0] : null,
             roomier: between ? null : next, bumped: bumped, fit: fit, usual: usual || null,
             tieByUsual: tieByUsual, usualGap: usualGap };
  }

  // LAB-168 (2026-10-21): Standard already sits relaxed (median +20 cm around the body), so +7 cm used to land on
  // oversized sizes (180 cm / 80 kg -> 2XL, worn and found too large; owner: XL is right). Roomier moves up only while the
  // bigger size leaves at most ROOMIER_MAX_EASE cm; otherwise it keeps Standard and names the bigger size as oversized.
  function copyOf(o) { var c = {}; for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) c[k] = o[k]; return c; }
  function teeRoom(size, body) { for (var i = 0; i < ADULT.length; i++) if (ADULT[i][0] === size) return 2 * ADULT[i][1] - body; return 0; }
  // A T-shirt the shopper owns and likes (LAB-168): its chest circumference replaces the body estimate; toss-up to the
  // bigger size, then the same length floor. Their own T-shirt carries their fit preference, so the toggle is not used.
  function teeFromRef(h, ref) {
    var ranked = ADULT.slice().sort(function (a, b) { return Math.abs(2 * a[1] - ref) - Math.abs(2 * b[1] - ref); });
    var best = ranked[0], second = ranked[1];
    if (Math.abs(2 * second[1] - ref) - Math.abs(2 * best[1] - ref) < BETWEEN_CM && second[1] > best[1]) best = second;
    var bumped = false;
    while (best[2] < LENGTH_FLOOR * h && ADULT.indexOf(best) < ADULT.length - 1) { best = ADULT[ADULT.indexOf(best) + 1]; bumped = true; }
    var next = ADULT.indexOf(best) < ADULT.length - 1 ? ADULT[ADULT.indexOf(best) + 1][0] : null;
    return { size: best[0], flat: best[1], length: best[2], between: false, alt: null, roomier: next, bumped: bumped, fit: 'ref',
             usual: null, tieByUsual: false, usualGap: 0 };
  }
  function recommendAdult(h, w, fit, usual, ref) {
    if (ref != null) {
      var rr = teeFromRef(h, ref), b0 = bodyChest(h, w);
      rr.roomCapped = false; rr.roomierOversized = !!(rr.roomier && teeRoom(rr.roomier, b0) > ROOMIER_MAX_EASE); rr.ref = ref;
      return rr;
    }
    var r = teePick(h, w, fit, usual), body = bodyChest(h, w), roomCapped = false;
    if (fit === 'relaxed') {
      var s = teePick(h, w, 'regular', usual), i = SIZES.indexOf(r.size), i0 = SIZES.indexOf(s.size);
      if (i > i0 && teeRoom(r.size, body) > ROOMIER_MAX_EASE) {
        while (i > i0 && teeRoom(SIZES[i], body) > ROOMIER_MAX_EASE) i -= 1;
        if (i === i0) r = copyOf(s);
        else { r = copyOf(r); r.size = SIZES[i]; r.flat = ADULT[i][1]; r.length = ADULT[i][2]; r.between = false; r.alt = null; r.roomier = SIZES[i + 1]; r.bumped = false; }
        r.fit = 'relaxed'; roomCapped = true;
      }
    }
    r.roomCapped = roomCapped;
    r.roomierOversized = !!(r.roomier && teeRoom(r.roomier, body) > ROOMIER_MAX_EASE);
    return r;
  }

  function recommendKidIn(h, table) {
    for (var i = 0; i < table.length; i++) if (h >= table[i][1] && h < table[i][2]) return { size: String(table[i][0]), lo: table[i][1], hi: table[i][2] };
    var top = table[table.length - 1], bottom = table[0];
    if (h >= top[2]) return { size: null, note: 'Above our largest kids size (' + top[0] + '). An adult ' + (top[0] >= 170 ? 'S' : 'XS') + ' is the next step.' };
    return { size: null, note: 'Below our smallest kids size (' + bottom[0] + ').' };
  }
  function recommendKid(h) { return recommendKidIn(h, KIDS); }

  // v2 (LAB-168, 2026-10-21): every top takes the tee size as the anchor, then the cut decides. Relaxed / Regular /
  // Cropped / Slim = the tee letter; Boxy / Oversized = the tee letter; Oversized only (v2.1), one down when it leaves more than BIG_TRIGGER
  // cm (Roomier keeps the Standard tee letter, worn full). Always clamped to the sizes the product really has. Jackets
  // (JACKET_CODES) step up for room over a layer and for the length floor on their own chart. Any other top whose size is
  // smaller than the body + its minimum ease steps up while a bigger size exists. The old "Regular" chest rule (3XL polo
  // for 180/80, XL women's T-shirt for everyone) is gone. Fleece charts never drive the rule (S for everyone, 09-12).
  function recommendGarment(h, w, fit, usual, ctx) {
    var big = STEP_DOWN_CUTS.indexOf(ctx.cut) >= 0;
    var t = recommendAdult(h, w, big ? 'regular' : fit, usual, ctx.ref != null ? ctx.ref : null);
    var sizes = ctx.sizes.length ? ctx.sizes : SIZES, table = ctx.table || [];
    function row(size) { for (var i = 0; i < table.length; i++) if (String(table[i][0]) === size) return table[i]; return null; }
    function chest(size) { var x = row(size); return x ? x[1] : null; }
    function length(size) { var x = row(size); return x ? x[2] : null; }
    var body = bodyChest(h, w);
    var minEase = (ctx.body === 'women' && !big) ? FIT_MIN_EASE_WOMEN : FIT_MIN_EASE;
    var pick, capped = false, floored = false, steppedDown = false, bumped = false, layered = false, fitBumped = false;
    var ti = SIZES.indexOf(t.size), lo = SIZES.indexOf(sizes[0]), hi = SIZES.indexOf(sizes[sizes.length - 1]);
    if (ti < 0) pick = t.size;
    else if (lo >= 0 && ti < lo) { pick = sizes[0]; floored = true; }
    else if (hi >= 0 && ti > hi) { pick = sizes[sizes.length - 1]; capped = true; }
    else pick = SIZES[ti];
    if (sizes.indexOf(pick) < 0) pick = sizes[Math.min(sizes.length - 1, Math.max(0, ti - (lo < 0 ? 0 : lo)))];
    if (ONE_DOWN_CUTS.indexOf(ctx.cut) >= 0 && fit !== 'relaxed' && !capped && table.length) {
      var pi = sizes.indexOf(pick), cur = row(pick), dn = pi > 0 ? row(sizes[pi - 1]) : null;
      if (cur && cur[1] != null && 2 * cur[1] - body > BIG_TRIGGER && dn && dn[1] != null && 2 * dn[1] - body >= BIG_MIN_EASE && longEnough(dn, h)) {
        pick = sizes[pi - 1]; steppedDown = true; floored = false;
      }
    }
    var i = sizes.indexOf(pick);
    if (JACKET_CODES.indexOf(ctx.blank) >= 0) {
      while (chest(pick) != null && 2 * chest(pick) < body + JACKET_MIN_ROOM && i < sizes.length - 1) { i += 1; pick = sizes[i]; layered = true; floored = false; }
      while (length(pick) != null && length(pick) < JACKET_LENGTH_FLOOR * h && i < sizes.length - 1) { i += 1; pick = sizes[i]; bumped = true; floored = false; }
    } else if (!ctx.stretch) {
      while (chest(pick) != null && 2 * chest(pick) < body + minEase && i < sizes.length - 1) { i += 1; pick = sizes[i]; fitBumped = true; floored = false; steppedDown = false; }
    }
    var r = row(pick) || [pick, null, null, null];
    var down = i > 0 ? sizes[i - 1] : null, up = i < sizes.length - 1 ? sizes[i + 1] : null;
    var tooSmall = !ctx.stretch && r[1] != null && 2 * r[1] < body + minEase;
    if (tooSmall) capped = true;                                                  // only the largest can still be too small
    var closer = null, roomier = null;
    if (!capped && !tooSmall) {
      if (big && steppedDown) roomier = up;                                       // the tee letter, for the full oversized look
      else if (big) { var dr = down ? row(down) : null; closer = (dr && dr[1] != null && 2 * dr[1] - body >= FIT_MIN_EASE && longEnough(dr, h)) ? down : (dr ? null : down); }
      else if (up) roomier = up;
    }
    return { size: pick, flat: r[1], length: r[2], shoulder: r[3], garment: true, cut: ctx.cut, teeSize: t.size,
             room: r[1] != null ? Math.round(2 * r[1] - body) : null,
             closer: closer, roomier: roomier, capped: capped, floored: floored, steppedDown: steppedDown, tooSmall: tooSmall,
             layered: layered, fitBumped: fitBumped,
             xl2xl: capped && pick === 'XL' && big && t.size === '2XL',          // the XL ≈ 2XL claim only for a 2XL tee body
             between: false, bumped: bumped, usual: usual || null, usualGap: 0, tieByUsual: false, stretch: !!ctx.stretch };
  }

  function r1(x) { return Math.round(x * 10) / 10; }
  function bodyBottom(h, w, body) {
    var f = BODY_FIT[body] || BODY_FIT.men, a = f.waist, b = f.hip;
    return { waist: r1(a[0] + a[1] * h + a[2] * w + a[3]), hip: r1(b[0] + b[1] * h + b[2] * w + b[3]) };
  }
  function band(v, bands) { for (var i = 0; i < bands.length; i++) if (v <= bands[i][1]) return i; return bands.length - 1; }
  function recommendBottom(h, w, fit, body, ctx) {
    var bb = bodyBottom(h, w, body), sizes = ctx.sizes.slice(), table = ctx.table || [];
    var numeric = sizes.length > 0 && sizes.every(function (x) { return /^\d+$/.test(x); });
    var order = numeric ? NUMERIC_HIP.map(function (x) { return x[0]; }) : SIZES;
    var idx = numeric ? band(bb.hip, NUMERIC_HIP) : Math.max(band(bb.waist, LETTER_WAIST[body] || LETTER_WAIST.men), band(bb.hip, LETTER_HIP[body] || LETTER_HIP.men));
    if (fit === 'relaxed') idx += 1;
    var std = order[Math.min(idx, order.length - 1)];
    var cand = sizes.filter(function (x) { return order.indexOf(x) >= 0; });
    var capped = false, floored = false, pick;
    if (!cand.length) pick = sizes[Math.floor(sizes.length / 2)];
    else if (order.indexOf(std) > order.indexOf(cand[cand.length - 1])) { pick = cand[cand.length - 1]; capped = true; }
    else if (order.indexOf(std) < order.indexOf(cand[0])) { pick = cand[0]; floored = true; }
    else { for (var c = 0; c < cand.length; c++) if (order.indexOf(cand[c]) >= order.indexOf(std)) { pick = cand[c]; break; } }
    function row(size) { for (var i = 0; i < table.length; i++) if (String(table[i][0]) === size) return table[i]; return null; }
    var hipBumped = false;
    while (true) {
      var rr = row(pick);
      if (!rr || rr[2] == null || 2 * rr[2] >= bb.hip + BOTTOM_MIN_HIP_EASE) break;
      var i0 = sizes.indexOf(pick);
      if (i0 === sizes.length - 1) { capped = true; break; }
      pick = sizes[i0 + 1]; hipBumped = true; floored = false;
    }
    var r = row(pick) || [pick, null, null, null];
    var tooSmall = r[2] != null && 2 * r[2] < bb.hip;
    var shortLeg = r[3] != null && r[3] >= 70 && r[3] < BOTTOM_LEN_FRAC * h;
    var i = sizes.indexOf(pick), up = i < sizes.length - 1 ? sizes[i + 1] : null;
    return { size: pick, std: std, bodyWaist: bb.waist, bodyHip: bb.hip, waist: r[1], hip: r[2], length: r[3], bottom: true,
             capped: capped, floored: floored, hipBumped: hipBumped, tooSmall: tooSmall, shortLeg: shortLeg,
             roomier: (capped || tooSmall) ? null : up, room: r[2] != null ? Math.round(2 * r[2] - bb.hip) : null,
             garment: true, cut: ctx.cut, between: false, bumped: false, usual: null, usualGap: 0, tieByUsual: false };
  }

  function cmToIn(cm) { return Math.round(cm / 2.54 * 2) / 2; }
  function fmtLen(cm, units) { return units === 'imperial' ? cmToIn(cm) + ' in' : cm + ' cm'; }

  function track(name, props) {
    try {
      (window.__lbReco = window.__lbReco || []).push({ e: name, p: props || {} });
      if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, props || {});
    } catch (e) { /* analytics must never break the picker */ }
  }
  function readStore() { try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {}; } catch (e) { return {}; } }
  function writeStore(o) { try { localStorage.setItem(STORE_KEY, JSON.stringify(o)); } catch (e) { /* private mode */ } }
  function num(el) { if (!el) return null; var v = parseFloat(el.value); return isFinite(v) ? v : null; }
  function el(tag, cls, text) { var e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }

  function init(root) {
    if (!root || root.hasAttribute('data-lb-ready')) return;
    root.setAttribute('data-lb-ready', '');
    var family = root.dataset.family;
    var opts = { adult: root.dataset.adultOpt || '', kid: root.dataset.kidsOpt || '' };
    var product = root.dataset.product;
    var picker = document.querySelector('variant-picker');
    var store = readStore();
    var units = store.units || root.dataset.units || 'metric';
    var q = function (sel) { return root.querySelector(sel); };
    var qi = function (name) { return root.querySelector('[data-in="' + name + '"]'); };
    var entry = q('.lb-reco__entry'), panel = q('.lb-reco__panel');
    var remembered = false;
    var unitsBtn = q('[data-units-toggle]'), errorEl = q('[data-error]'), resultEl = q('[data-result]'), announce = q('[data-announce]');
    var goBtn = q('[data-go]'), fitHint = q('[data-fit-hint]');
    var last = { adult: null, kid: null };
    // per-product context rendered by the snippet (v1.4): the garment's own chart and size list, the cut, kids sizes
    var ctx = { cut: root.dataset.cut || 'Relaxed', tee: root.dataset.tee === 'true', table: [], sizes: [], kids: KIDS,
                bottom: root.dataset.kind === 'B', stretch: root.dataset.kind === 'S', body: root.dataset.body || 'unisex', blank: root.dataset.blank || '', ref: null };
    try { ctx.table = JSON.parse(root.dataset.table || '[]') || []; } catch (e) { ctx.table = []; }
    ctx.sizes = (root.dataset.sizes || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    var kidsList = (root.dataset.kidsSizes || '').split(',').map(function (x) { return parseInt(x, 10); }).filter(function (n) { return n > 0; });
    if (kidsList.length) ctx.kids = kidsList.map(function (n) { return [n, n - 5, n + 5]; });
    var lastInputs = {};
    // LAB-168: "Match a T-shirt you own": one of our researched T-shirts (REF_TEES) or the shopper's own flat measurement.
    var refSel = qi('ref'), refSizeSel = qi('ref-size'), refSizeField = q('[data-ref-size]'), refMeasure = q('[data-ref-measure]'), refHint = q('[data-ref-hint]');
    function refTee(id) { for (var i = 0; i < REF_TEES.length; i++) if (REF_TEES[i].id === id) return REF_TEES[i]; return null; }
    function hintText() {
      if (ctx.bottom) return 'Roomier goes one size up.';
      if (refSel && refSel.value) return 'Your T-shirt sets the fit.';
      return units === 'metric' ? 'Roomier adds up to 7 cm around the chest, short of oversized.' : 'Roomier adds up to 3 in around the chest, short of oversized.';
    }
    if (refSel) {
      var groups = {}, measureOpt = refSel.querySelector('option[value="measure"]');
      REF_TEES.forEach(function (t) {
        var g = groups[t.group];
        if (!g) { g = groups[t.group] = document.createElement('optgroup'); g.label = t.group; refSel.insertBefore(g, measureOpt); }
        var o = document.createElement('option'); o.value = t.id; o.textContent = t.name; g.appendChild(o);
      });
    }
    function refShow() {
      if (!refSel) return;
      var v = refSel.value, t = refTee(v);
      refSizeField.hidden = !t; refMeasure.hidden = v !== 'measure'; refHint.hidden = v !== 'measure';
      if (t) {
        var keep = refSizeSel.value; refSizeSel.innerHTML = '';
        var o0 = document.createElement('option'); o0.value = ''; o0.textContent = 'Choose'; refSizeSel.appendChild(o0);
        t.sizes.forEach(function (sz) { var o = document.createElement('option'); o.value = sz[0]; o.textContent = sz[0]; refSizeSel.appendChild(o); });
        refSizeSel.value = keep && t.sizes.some(function (sz) { return sz[0] === keep; }) ? keep : '';
      }
      if (fitHint) fitHint.textContent = hintText();
      var fitSet = q('fieldset[data-fit]'); if (fitSet && !ctx.bottom) fitSet.disabled = !!v;   // the shopper's T-shirt sets the fit
    }
    function refValue() {   // null (not used) | {error} | {around, id, size, name, flat}
      if (!refSel || !refSel.value) return null;
      if (refSel.value === 'measure') {
        var inch = num(qi('ref-in')), flat = units === 'metric' ? num(qi('ref-cm')) : (inch == null ? null : inch * 2.54);
        if (flat == null) return { error: 'Enter the armpit-to-armpit width, or choose a T-shirt.' };
        if (flat < 35 || flat > 90) return { error: units === 'imperial' ? 'Armpit to armpit is usually 16 to 30 in, laid flat.' : 'Armpit to armpit is usually 40 to 75 cm, laid flat.' };
        flat = Math.round(flat * 10) / 10;
        return { around: Math.round(2 * flat * 10) / 10, id: 'measure', size: null, name: null, flat: flat };
      }
      var t = refTee(refSel.value); if (!t) return null;
      var sz = refSizeSel.value;
      if (!sz) return { error: 'Choose the size of your ' + t.name + '.' };
      for (var i = 0; i < t.sizes.length; i++) if (t.sizes[i][0] === sz) return { around: t.sizes[i][1], id: t.id, size: sz, name: t.name, flat: null };
      return null;
    }
    function refLine(info) {
      var a = units === 'imperial' ? cmToIn(info.around) + ' in' : info.around + ' cm';
      return info.name ? 'Matched to your ' + info.name + ' in ' + info.size + ', ' + a + ' around.' : 'Matched to your T-shirt, ' + a + ' around.';
    }

    function setUnits(u) {
      units = u; root.dataset.units = u;
      var kidsOnly = family === 'kids';
      unitsBtn.textContent = u === 'metric' ? (kidsOnly ? 'Use ft / in' : 'Use ft / lb') : (kidsOnly ? 'Use cm' : 'Use cm / kg');
      unitsBtn.setAttribute('aria-label', u === 'metric' ? 'Switch to feet and pounds' : 'Switch to centimetres and kilograms');
      if (fitHint) fitHint.textContent = hintText();
      store.units = u; writeStore(store);
    }

    function fillFromStore() {
      if (store.h_cm && qi('h-cm')) qi('h-cm').value = store.h_cm;
      if (store.w_kg && qi('w-kg')) qi('w-kg').value = store.w_kg;
      if (store.h_cm && qi('h-ft')) { qi('h-ft').value = Math.floor(store.h_cm / 30.48); qi('h-in').value = Math.round(store.h_cm / 2.54 - Math.floor(store.h_cm / 30.48) * 12); }
      if (store.w_kg && qi('w-lb')) qi('w-lb').value = Math.round(store.w_kg * 2.2046);
      if (store.kh_cm && qi('kh-cm')) qi('kh-cm').value = store.kh_cm;
      if (store.kh_cm && qi('kh-ft')) { qi('kh-ft').value = Math.floor(store.kh_cm / 30.48); qi('kh-in').value = Math.round(store.kh_cm / 2.54 - Math.floor(store.kh_cm / 30.48) * 12); }
      if (store.fit) { var f = q('input[name="lb-reco-fit"][value="' + store.fit + '"]'); if (f) f.checked = true; }
      if (store.body) { var bd = q('input[name="lb-reco-body"][value="' + store.body + '"]'); if (bd) bd.checked = true; }
      if (store.ref && refSel && (store.ref.id === 'measure' || refTee(store.ref.id))) {
        refSel.value = store.ref.id; refShow(); if (store.ref.size) refSizeSel.value = store.ref.size;
        if (store.ref.flat) { if (qi('ref-cm')) qi('ref-cm').value = store.ref.flat; if (qi('ref-in')) qi('ref-in').value = cmToIn(store.ref.flat); }
        var det = q('[data-ref]'); if (det) det.open = true;
      }
    }

    function heightCm(prefix) {
      if (units === 'metric') return num(qi(prefix + '-cm'));
      var ft = num(qi(prefix + '-ft')), inch = num(qi(prefix + '-in')) || 0;
      return ft == null ? null : Math.round((ft * 12 + inch) * 2.54);
    }
    function weightKg() {
      if (units === 'metric') return num(qi('w-kg'));
      var lb = num(qi('w-lb')); return lb == null ? null : Math.round(lb / 2.2046);
    }
    function fitValue() { var f = q('input[name="lb-reco-fit"]:checked'); return f ? f.value : 'regular'; }
    function bodyValue() {
      if (ctx.body === 'men' || ctx.body === 'women') return ctx.body;
      var b = q('input[name="lb-reco-body"]:checked'); return b ? b.value : null;
    }

    function sizeRadio(kind, size) {
      if (!picker || !size) return null;
      var radios = picker.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        var r = radios[i];
        if (r.value !== size) continue;
        var name = r.getAttribute('data-option-name') || '';
        if (kind === 'kid' && opts.kid && name === opts.kid) return r;
        if (kind === 'adult' && opts.adult && name === opts.adult) return r;
        if (!name) return r;
      }
      return null;
    }
    function isAvailable(radio) { return !radio || radio.getAttribute('data-option-available') !== 'false'; }

    // ---- marker on the size button (attribute only; the label's text stays untouched for PostHog) ----
    var marking = false;
    function applyMarks() {
      if (!picker || marking) return;
      marking = true;
      try {
        var wanted = [];
        ['adult', 'kid'].forEach(function (kind) { if (last[kind] && last[kind].size) { var r = sizeRadio(kind, last[kind].size); if (r) wanted.push(r); } });
        picker.querySelectorAll('label[data-lb-your-size]').forEach(function (lab) {
          var inp = lab.querySelector('input[type="radio"]');
          if (wanted.indexOf(inp) === -1) { lab.removeAttribute('data-lb-your-size'); if (inp) inp.removeAttribute('aria-describedby'); }
        });
        wanted.forEach(function (r) {
          var lab = r.closest('label');
          if (lab && !lab.hasAttribute('data-lb-your-size')) lab.setAttribute('data-lb-your-size', '');
          if (r.getAttribute('aria-describedby') !== 'lb-reco-mark') r.setAttribute('aria-describedby', 'lb-reco-mark');
        });
      } finally { marking = false; }
    }
    var rafPending = false;
    function scheduleMarks() { if (rafPending) return; rafPending = true; requestAnimationFrame(function () { rafPending = false; applyMarks(); refreshSelectButtons(); }); }
    if (picker) {
      new MutationObserver(function () { scheduleMarks(); }).observe(picker, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-lb-your-size', 'aria-describedby', 'checked'] });
      picker.addEventListener('change', function (ev) {
        var t = ev.target; if (!t || t.type !== 'radio') return;
        var kind = ((opts.kid && t.getAttribute('data-option-name') === opts.kid) || /^\d+$/.test(t.value)) ? 'kid' : 'adult';
        if (last[kind] && last[kind].size === t.value && !t.hasAttribute('data-lb-selecting')) track('size_reco_followed', { product: product, size: t.value, kid: kind === 'kid', via: 'tap' });
        scheduleMarks();
      });
    }
    function refreshSelectButtons() {
      resultEl.querySelectorAll('.lb-reco__select').forEach(function (b) {
        var r = sizeRadio(b.dataset.kind, b.dataset.size);
        var on = !!(r && r.checked);
        b.textContent = on ? 'Selected' : 'Select ' + b.dataset.size;
        if (on) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
      });
    }
    function select(kind, size) {
      var r = sizeRadio(kind, size);
      if (!r) return;
      // LAB-154: click even when already checked, so the sticky gate (and the main button) count it as chosen
      r.setAttribute('data-lb-selecting', ''); r.click(); r.removeAttribute('data-lb-selecting');
      track('size_reco_followed', { product: product, size: size, kid: kind === 'kid', via: 'select' });
      announce.textContent = size + ' selected.';
      scheduleMarks();
    }

    // ---- result cards ----
    function adultLine(r) {
      if (r.bottom) {
        if (r.hip == null) return r.cut + ' fit.';
        var cm = function (v) { return units === 'imperial' ? cmToIn(v) + ' in' : v + ' cm'; };
        return 'Waist ' + cm(2 * r.waist) + ' around before it stretches, hip ' + cm(2 * r.hip) + ' around, length ' + cm(r.length) + '.';
      }
      if (r.garment) {
        if (r.flat == null) return r.cut + ' fit.';
        var around = units === 'imperial' ? cmToIn(2 * r.flat) + ' in' : (2 * r.flat) + ' cm', len = units === 'imperial' ? cmToIn(r.length) + ' in' : r.length + ' cm';
        return 'Chest ' + around + ' around, length ' + len + '.';
      }
      return units === 'imperial' ? 'Chest ' + cmToIn(r.flat) + ' in flat, length ' + cmToIn(r.length) + ' in.' : 'Chest ' + r.flat + ' cm flat, length ' + r.length + ' cm.';
    }
    function cutLine(r) {
      if (r.bottom) {
        if (r.tooSmall) return 'Our largest in this cut, and likely snug at the hip for you.';
        if (r.capped) return 'Our largest in this cut.';
        if (r.floored) return 'Our smallest in this cut.';
        var t = r.hipBumped ? 'One up from your usual ' + r.std + ': this cut is close at the hip.' : 'Your usual size.';
        if (r.room != null) t += ' About ' + (units === 'imperial' ? cmToIn(r.room) + ' in' : r.room + ' cm') + ' of room at the hip.';
        if (r.shortLeg) t += ' At your height it sits above the ankle.';
        return t;
      }
      if (r.stretch && !r.capped && !r.floored) return 'Your usual size. A stretch fabric cut close to the body, so it measures less around than you do.';
      if (r.tooSmall) return 'Our largest in this cut, and likely too snug for you: it measures less around the chest than we would suggest.';
      if (r.capped) return 'Our largest in this cut.' + (r.xl2xl ? ' It wears like a regular 2XL.' : '');
      if (r.steppedDown) return 'Cut ' + (r.cut === 'Oversized' ? 'oversized' : 'boxy') + ', so ' + r.size + ' already leaves about ' + r.room + ' cm of room around you.';
      if (r.layered) return 'One up, so there is room for a layer underneath.';
      if (r.fitBumped) return 'One up: this cut sits close to the body.';
      if (r.bumped) return 'One up for the length at your height.';
      if (r.floored) return 'Our smallest in this cut.';
      if (r.cut === 'Boxy') return 'Your usual size. Cut boxy' + (r.room != null ? ': about ' + r.room + ' cm of room around you.' : ', wide and a little short.');
      if (r.cut === 'Oversized') return 'Your usual size. Cut oversized' + (r.room != null ? ': about ' + r.room + ' cm of room around you.' : ', roomy everywhere.');
      if (r.cut === 'Cropped') return 'Sits at the waist.';
      if (r.cut === 'Slim') return 'Close to the body; between sizes, take the larger.';
      if (r.cut === 'Regular') return 'True to size.';
      return 'Your tee size. Runs roomy.';
    }
    function altFor(r, h) {
      if (r.garment) {
        if (r.closer) return { text: 'One down sits closer. Select ' + r.closer + '.', action: r.closer };
        if (r.steppedDown && r.roomier) return { text: r.roomier + ', your usual, is 4 cm roomier. Select ' + r.roomier + '.', action: r.roomier };
        if (r.roomier) return { text: 'Prefer it roomier? Select ' + r.roomier + '.', action: r.roomier };
        return null;
      }
      if (r.bumped) { var idx = SIZES.indexOf(r.size); var chestSize = SIZES[Math.max(0, idx - 1)]; return { text: chestSize + ' fits the chest. At ' + fmtLen(h, units) + ' you want the length, so ' + r.size + '.', action: null }; }
      if (r.between) return { text: r.alt + ' is the closer fit. Select ' + r.alt + '.', action: r.alt };
      if (r.roomier) return { text: r.roomierOversized ? r.roomier + ' is an oversized look on you. Select ' + r.roomier + '.' : 'Prefer it roomier? Select ' + r.roomier + '.', action: r.roomier };
      return null;
    }
    function card(kind, r, h) {
      var c = el('div', 'lb-reco__card'); c.setAttribute('data-reco', kind); c.setAttribute('data-size', r.size || '');
      c.appendChild(el('p', 'lb-reco__eyebrow', family === 'pair' ? (kind === 'kid' ? 'Kid' : 'Adult') : 'Try'));
      if (!r.size) { c.appendChild(el('p', 'lb-reco__line', r.note)); return c; }
      var fig = el('p', 'lb-reco__figure', r.size); fig.setAttribute('aria-hidden', 'true'); c.appendChild(fig);
      c.appendChild(el('p', 'lb-reco__line', kind === 'kid' ? 'Named by height. Fits ' + fmtLen(r.lo, units) + ' to ' + fmtLen(r.hi, units) + '.' : adultLine(r)));
      if (kind === 'adult' && r.refInfo) c.appendChild(el('p', 'lb-reco__line', refLine(r.refInfo)));
      if (kind === 'adult' && r.garment) { var cl = el('p', 'lb-reco__line', cutLine(r)); cl.setAttribute('data-cut-line', ''); c.appendChild(cl); }
      var radio = sizeRadio(kind, r.size);
      if (isAvailable(radio)) {
        var b = el('button', 'button-secondary lb-reco__select', 'Select ' + r.size); b.type = 'button'; b.dataset.kind = kind; b.dataset.size = r.size;
        b.addEventListener('click', function () { if (b.getAttribute('aria-disabled') !== 'true') select(kind, r.size); });
        c.appendChild(b);
      } else c.appendChild(el('p', 'lb-reco__line', r.size + ' is sold out right now.'));
      if (kind === 'adult') {
        var a = altFor(r, h);
        if (a) {
          var p = el('p', 'lb-reco__alt');
          if (a.action) { var ab = el('button', 'lb-reco__alt-btn', a.text); ab.type = 'button'; ab.addEventListener('click', function () { select('adult', a.action); }); p.appendChild(ab); }   // the whole sentence is the action
          else p.textContent = a.text;
          c.appendChild(p);
        }
      }
      return c;
    }

    function showError(msg) { errorEl.textContent = msg; errorEl.hidden = !msg; root.querySelectorAll('.lb-reco__input input').forEach(function (i) { if (msg) i.setAttribute('aria-invalid', 'true'); else i.removeAttribute('aria-invalid'); }); }

    function run(source) {
      var cards = [], sentences = [], errors = [];
      var props = { product: product, family: family, units: units, source: source || 'form' };
      last = { adult: null, kid: null };
      if (family !== 'kids') {
        var h = heightCm('h'), w = weightKg(), fit = fitValue(), usual = null;   // the usual-size chips were removed 2026-09-16 (owner): confusing, no effect on the answer
        var body = ctx.bottom ? bodyValue() : null;
        var refv = ctx.bottom ? null : refValue();
        if (h == null || w == null) errors.push(family === 'pair' ? "Enter the adult's height and weight." : 'Enter a height and weight to get a size.');
        else if (ctx.bottom && !body) errors.push("Choose men's or women's sizing.");
        else if (h < 120 || h > 220 || w < 30 || w > 200) errors.push(units === 'imperial' ? 'Outside the chart. It covers 3 ft 11 to 7 ft 3 and 66 to 440 lb.' : 'Outside the chart. It covers 120 to 220 cm and 30 to 200 kg.');
        else if (refv && refv.error) errors.push(refv.error);
        else {
          ctx.ref = refv ? refv.around : null;
          var r = ctx.bottom ? recommendBottom(h, w, fit, body, ctx) : ctx.tee ? recommendAdult(h, w, fit, usual, ctx.ref) : recommendGarment(h, w, fit, usual, ctx); if (refv) r.refInfo = refv; last.adult = r; lastInputs.h = h; lastInputs.w = w;
          cards.push(card('adult', r, h));
          sentences.push((family === 'pair' ? 'Adult: try ' : 'Try ') + r.size + '. ' + adultLine(r) + (r.garment ? ' ' + cutLine(r) + (r.closer ? ' One down sits closer: ' + r.closer + '.' : (r.steppedDown && r.roomier ? ' Your tee size for the full look: ' + r.roomier + '.' : '')) : (r.between ? ' ' + r.alt + ' is the closer fit.' : r.roomier ? (r.roomierOversized ? ' ' + r.roomier + ' is an oversized look.' : ' Prefer it roomier? ' + r.roomier + '.') : '')));
          props.cut = ctx.cut; props.tee_size = r.teeSize || r.size; props.capped = !!r.capped; props.too_small = !!r.tooSmall; props.stepped_down = !!r.steppedDown; props.room_capped = !!r.roomCapped; props.fit_bumped = !!r.fitBumped; props.layered = !!r.layered;
          props.ref = refv ? (refv.id === 'measure' ? 'measured' : refv.id) : null; props.fit = fit; props.size = r.size; props.between = r.between; props.length_bumped = r.bumped;
          if (ctx.bottom) { props.bottom = true; props.body = body; props.std_size = r.std; props.hip_bumped = !!r.hipBumped; if (ctx.body === 'unisex') store.body = body; }
          store.h_cm = h; store.w_kg = w; store.fit = fit; store.h_ts = Date.now(); store.ref = refv ? { id: refv.id, size: refv.size, flat: refv.flat } : null;
        }
      }
      if (family !== 'adult') {
        var kh = heightCm('kh');
        if (kh == null) errors.push(family === 'pair' ? "Enter the kid's height." : 'Enter a height to get a size.');
        else if (kh < 90 || kh > 200) errors.push(units === 'imperial' ? 'Outside the kids chart. It covers 2 ft 11 to 6 ft 7.' : 'Outside the kids chart. It covers 90 to 200 cm.');
        else {
          var k = recommendKidIn(kh, ctx.kids); last.kid = k; lastInputs.kh = kh;
          cards.push(card('kid', k, kh));
          sentences.push(k.size ? (family === 'pair' ? 'Kid: try ' : 'Try ') + k.size + '. Named by height, fits ' + k.lo + ' to ' + k.hi + ' cm.' : k.note);
          props.kid_size = k.size;
          store.kh_cm = kh; store.kh_ts = Date.now();
        }
      }
      if (last.adult || last.kid) {   // LAB-106: the cart's edit sheet names this answer (assets/lb-cart-edit.js)
        var recs = store.recs || {}; recs[product] = { a: last.adult ? last.adult.size : null, k: last.kid ? last.kid.size : null, t: Date.now() };
        var keys = Object.keys(recs).sort(function (x, y) { return recs[y].t - recs[x].t; }); keys.slice(30).forEach(function (k) { delete recs[k]; });
        store.recs = recs;
      }
      writeStore(store);
      showError(errors.join(' '));
      resultEl.innerHTML = '';
      cards.forEach(function (c) { resultEl.appendChild(c); });
      resultEl.hidden = cards.length === 0;
      announce.textContent = (sentences.concat(errors)).join(' ');
      if (props.size || props.kid_size) track('size_reco_result', props);
      applyMarks(); refreshSelectButtons();
    }

    // ---- remembered profile: the CTA stays (owner 2026-09-05); the result is precomputed and the size button marked ----
    function hasProfile() {
      var adultOk = !!(store.h_cm && store.w_kg) && (!ctx.bottom || ctx.body !== 'unisex' || !!store.body), kidOk = !!store.kh_cm;
      return family === 'adult' ? adultOk : family === 'kids' ? kidOk : (adultOk && kidOk);
    }
    function openPanel(focusFirst) {
      panel.hidden = false; entry.setAttribute('aria-expanded', 'true'); unitsBtn.hidden = false;
      if (focusFirst) { var first = root.querySelector('[data-unit="' + units + '"] input'); if (first) first.focus(); }
      else { var sel = resultEl.querySelector('.lb-reco__select'); if (sel) sel.focus(); }
      stickyFit();
    }
    function closePanel() { panel.hidden = true; entry.setAttribute('aria-expanded', 'false'); unitsBtn.hidden = true; stickyFit(); }

    // ---- wiring ----
    setUnits(units);
    fillFromStore();
    entry.addEventListener('click', function () {
      if (panel.hidden) { openPanel(!remembered); track('size_reco_opened', { product: product, family: family, remembered: remembered }); }
      else closePanel();
    });
    goBtn.addEventListener('click', function () { run('form'); });
    if (refSel) {
      refSel.addEventListener('change', function () { refShow(); if (!resultEl.hidden) run('form'); });
      refSizeSel.addEventListener('change', function () { if (!resultEl.hidden) run('form'); });
    }
    var forgetBtn = q('[data-forget]');
    if (forgetBtn) forgetBtn.addEventListener('click', function () {
      try { localStorage.removeItem(STORE_KEY); } catch (e) { /* private mode */ }
      store = { units: units };
      root.querySelectorAll('.lb-reco__panel input[type="number"]').forEach(function (i) { i.value = ''; });
      if (refSel) { refSel.value = ''; refShow(); }
      last = { adult: null, kid: null }; resultEl.innerHTML = ''; resultEl.hidden = true; showError('');
      applyMarks(); refreshSelectButtons();
      announce.textContent = 'Forgotten. Nothing you entered is kept in this browser now.';
      track('size_reco_forget', { product: product });
    });
    unitsBtn.addEventListener('click', function () { setUnits(units === 'metric' ? 'imperial' : 'metric'); if (!resultEl.hidden) run('form'); });
    root.querySelectorAll('input[name="lb-reco-fit"], input[name="lb-reco-body"]').forEach(function (r) { r.addEventListener('change', function () { if (!resultEl.hidden) run('form'); }); });
    root.querySelectorAll('.lb-reco__pill input').forEach(function (r) {
      r.addEventListener('click', function () {
        if (r.dataset.wasChecked === '1') { r.checked = false; r.dataset.wasChecked = '0'; }
        else { root.querySelectorAll('.lb-reco__pill input').forEach(function (o) { o.dataset.wasChecked = '0'; }); r.dataset.wasChecked = '1'; }
        if (!resultEl.hidden) run('form');
      });
    });
    panel.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' && ev.target && ev.target.tagName === 'INPUT' && ev.target.type === 'number') { ev.preventDefault(); run('form'); }
    });

    if (hasProfile()) { run('auto'); remembered = !resultEl.hidden; }
    stickyFit();
  }

  // ---- desktop: a sticky details column taller than the viewport pins its lower part out of reach
  // (Horizon: .product-details.sticky-content--desktop, top: var(--sticky-header-offset, 0)). Sticky-bottom instead:
  // when the column is taller than the viewport, set a negative top so it scrolls until its bottom edge is in view,
  // then sticks while the gallery finishes. Re-measured on resize and whenever the column's height changes
  // (the finder opening, accordions). Owner-reported 2026-09-05; pre-existing on live, worse with the finder open.
  var stickyCol = null, stickyRO = null;
  function stickyFit() {
    var col = document.querySelector('.product-details.sticky-content--desktop');
    if (!col) return;
    if (col !== stickyCol) {
      stickyCol = col;
      if ('ResizeObserver' in window) { if (stickyRO) stickyRO.disconnect(); stickyRO = new ResizeObserver(function () { applySticky(col); }); stickyRO.observe(col); }
      window.addEventListener('resize', function () { applySticky(col); });
    }
    applySticky(col);
  }
  function applySticky(col) {
    if (getComputedStyle(col).position !== 'sticky') { col.style.top = ''; col.removeAttribute('data-lb-sticky-bottom'); return; }
    var h = col.offsetHeight, vh = window.innerHeight;
    var prev = col.style.top; col.style.top = '';                       // measure Horizon's own offset
    var base = parseFloat(getComputedStyle(col).top) || 0;
    if (h + base > vh) { col.style.top = Math.round(vh - h) + 'px'; col.setAttribute('data-lb-sticky-bottom', ''); }
    else { col.style.top = ''; col.removeAttribute('data-lb-sticky-bottom'); if (prev && prev === col.style.top) { /* unchanged */ } }
  }

  function boot() {
    document.querySelectorAll('[data-lb-reco]').forEach(init);
    new MutationObserver(function () { document.querySelectorAll('[data-lb-reco]:not([data-lb-ready])').forEach(init); }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  window.lbSizeReco = { recommendAdult: recommendAdult, recommendKid: recommendKid, recommendKidIn: recommendKidIn, recommendGarment: recommendGarment, recommendBottom: recommendBottom, bodyBottom: bodyBottom, bodyChest: bodyChest, ease: ease };
})();
